import { ProgressiveTierOffer } from '../../promotion-display-utils';
import { ProductAggregate } from '../aggregate-cart';
import { FamilyOutcome, UnitDiscount } from '../family-outcome.types';

/**
 * Computes the quantity (progressive) family outcome over a product aggregate.
 *
 * - Picks the applicable tier: the one with the highest `quantity` ≤ total units. Input
 *   `progressiveTiers` is expected to be a non-decreasing ladder (output of `mergeProgressiveTiers`),
 *   so the last qualifying entry is also the one with the highest percentage.
 * - The applicable tier's `percentage` applies to EVERY unit's basePrice individually
 *   (each unit gets its own `unitDiscount = basePrice × pct`).
 * - All discount is attributed to a single source: the tier's `sourcePromotionId`.
 *
 * No rounding here — totals stay raw for the orchestrator to round once at the end.
 * Pure and deterministic.
 */
export function calculateProgressiveForAggregate(
  progressiveTiers: ProgressiveTierOffer[],
  aggregate: ProductAggregate,
): FamilyOutcome {
  if (progressiveTiers.length === 0 || aggregate.totalUnits === 0) {
    return { family: 'progressive', totalDiscount: 0, perPromoBreakdown: [] };
  }

  let applicable: ProgressiveTierOffer | undefined;
  for (const tier of progressiveTiers) {
    if (tier.quantity <= aggregate.totalUnits) {
      applicable = tier;
    } else {
      break;
    }
  }

  if (!applicable || applicable.percentage <= 0) {
    return { family: 'progressive', totalDiscount: 0, perPromoBreakdown: [] };
  }

  const fraction = applicable.percentage / 100;
  const unitDiscounts: UnitDiscount[] = aggregate.units.map(unit => ({
    unit,
    amount: unit.basePrice * fraction,
    sourcePromotionId: applicable.sourcePromotionId,
  }));

  const discountAmount = unitDiscounts.reduce((sum, ud) => sum + ud.amount, 0);

  return {
    family: 'progressive',
    totalDiscount: discountAmount,
    perPromoBreakdown: [
      {
        promotionId: applicable.sourcePromotionId,
        discountAmount,
        unitDiscounts,
      },
    ],
  };
}
